// Q9 S5 / D11 — "Auto-saved Xs ago" badge.
//
// Spec (docs/specs/visit-report-save-state.md §"Auto-saved Xs ago" badge):
//   Source: lastSuccessfulPatch timestamp from the wizard's save state,
//   updated on every successful PATCH response. Time since computed
//   client-side via a 1Hz tick.
//
// State display:
//   Saving in flight  → "Saving…" (with subtle pulse)
//   Save persistently failing → null (RetryIndicator handles that surface)
//   Never saved       → "Not saved yet"
//   Default           → "Auto-saved {Xs|Nm|at HH:MM} ago"
//
// The badge ticks once per second so the relative-time string stays
// fresh; the tick effect only runs while there's a lastSavedAt to format
// (no idle-pump cost when the wizard hasn't saved anything yet).

import { useEffect, useState } from "react";
import type { JSX } from "react";
import { formatRelativeSaveTime } from "../lib/relative-time.js";

interface Props {
  lastSavedAt: Date | null;
  isSaving: boolean;
  /** True when there's at least one section in failedSections — the
   *  RetryIndicator owns that surface, so the badge stays silent. */
  hasFailedSections: boolean;
}

export function AutoSaveBadge({ lastSavedAt, isSaving, hasFailedSections }: Props): JSX.Element | null {
  // 1Hz tick — re-render to refresh the "Xs ago" string. Only runs while
  // there's something to display; tearing it down when no save has ever
  // happened keeps idle CPU at zero.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!lastSavedAt) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [lastSavedAt]);

  if (hasFailedSections) return null;

  if (isSaving) {
    return (
      <span
        data-testid="auto-save-badge"
        data-state="saving"
        className="text-xs text-slate-500 inline-flex items-center gap-1.5"
        aria-live="polite"
      >
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" />
        Saving…
      </span>
    );
  }

  if (!lastSavedAt) {
    return (
      <span
        data-testid="auto-save-badge"
        data-state="never-saved"
        className="text-xs text-slate-400"
      >
        Not saved yet
      </span>
    );
  }

  return (
    <span
      data-testid="auto-save-badge"
      data-state="saved"
      className="text-xs text-slate-500 inline-flex items-center gap-1.5"
      aria-live="polite"
    >
      <span aria-hidden="true">✓</span>
      Auto-saved {formatRelativeSaveTime(lastSavedAt)}
    </span>
  );
}
